import type { JSX } from 'react';
import { BrickWall, Hand, LayoutGrid, PenLine, MousePointer2, PaintBucket, Ruler, SquareStack } from 'lucide-react';
import { useEditor } from '../state/store.js';
import type { ToolId } from '../state/tools.js';
import { TOOL_META } from '../state/tools.js';
import { STAGE_TOOLS, stageOfTool } from '../state/stages.js';

const ICONS: Record<ToolId, typeof Hand> = {
  select: MousePointer2,
  // A trowel would be ideal and lucide has none; a brick wall says what this
  // tool lays, where the spanner it replaced said "settings".
  build: BrickWall,
  paint: PaintBucket,
  measure: Ruler,
  pan: Hand,
  paste: SquareStack,
  sketch: PenLine,
  plan: LayoutGrid,
};

/**
 * The tools of the current stage only.
 *
 * A flat rail of eight put "arrange the walls into a building" beside "paint a
 * brick", as though they were the same kind of choice at the same moment. The
 * rail now follows the step bar: drawing tools while you draw, and the plan
 * while you arrange. Nothing is lost — every tool keeps its shortcut and its
 * command-palette entry — but the rail stops offering a bricklaying trowel to
 * someone positioning walls on the ground.
 */
export function ToolRail({ run }: { readonly run: (id: string) => void }): JSX.Element {
  const tool = useEditor((state) => state.tool);
  const previousTool = useEditor((state) => state.previousTool);
  const clipboard = useEditor((state) => state.clipboard);
  const stage = stageOfTool(tool, previousTool);
  const order = STAGE_TOOLS[stage];
  return (
    <nav className="rail" aria-label="Tools">
      {order.map((id) => {
        const Icon = ICONS[id];
        return (
          <button
            key={id}
            aria-pressed={tool === id}
            aria-label={`${TOOL_META[id].label} (${TOOL_META[id].shortcut})`}
            title={`${TOOL_META[id].label} · ${TOOL_META[id].shortcut}`}
            onClick={() => run(`tool.${id}`)}
          >
            <Icon size={18} />
          </button>
        );
      })}
      {stage === 'draw' && (
        <button
          aria-pressed={tool === 'paste'}
          aria-label="Paste pattern (Ctrl+V)"
          title="Paste pattern · Ctrl+V"
          disabled={clipboard === null}
          style={clipboard === null ? { opacity: 0.35 } : undefined}
          onClick={() => run('edit.paste')}
        >
          <SquareStack size={18} />
        </button>
      )}
    </nav>
  );
}
